import { IUser, UserCourseStatus, UserRole } from '@core/interfaces';
import { Entity } from './entity';

export class User extends Entity<IUser> {
  constructor(props: IUser) {
    super(props);
  }

  get id() {
    return this.props.id;
  }

  get username() {
    return this.props.username;
  }

  get email() {
    return this.props.email;
  }

  get role() {
    return this.props.role;
  }

  get firstName() {
    return this.props.firstName ?? '';
  }

  get lastName() {
    return this.props.lastName ?? '';
  }

  get name() {
    return this.firstName + ' ' + this.lastName;
  }

  get shortName() {
    return (this.firstName?.[0] ?? '') + (this.lastName?.[0] ?? '');
  }

  get nickname() {
    return this.props.nickname ?? '';
  }

  get profileImage() {
    return this.props.profileImage;
  }

  get isProfileCompleted() {
    return this.props.firstName && this.props.lastName;
  }

  get configuredLanguage() {
    return this.props.configuredLanguage;
  }

  get lastAccessCourse() {
    return this.props.lastAccessCourse;
  }

  get lastLogin() {
    return this.props.lastLogin && new Date(this.props.lastLogin);
  }

  getLocaleLastLogin(locale: string | string[]) {
    return (
      this.props.lastLogin &&
      new Date(this.props.lastLogin).toLocaleString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    );
  }

  get courses() {
    return this.props.courses;
  }

  set courses(courseIds: number[]) {
    this.props.courses = courseIds;
  }

  get courseCount() {
    return this.props.courseCount;
  }

  get status() {
    return this.props.isActive;
  }

  get statusName() {
    return this.props.isActive ? 'Active' : 'Inactive';
  }

  get statusClass(): string {
    return this.props.isActive ? 'active' : 'inactive';
  }

  get statusBadgeClass(): string {
    return this.props.isActive ? 'badge-success' : 'badge-warning';
  }

  get isEmailVerified() {
    return this.props.isEmailVerified;
  }

  get isEmailVerifiedName() {
    return this.props.isEmailVerified ? 'Verified' : 'Pending';
  }

  get isAdmin(): boolean {
    return this.props.role === UserRole.Admin;
  }

  get isInstructor(): boolean {
    return this.props.role === UserRole.Instructor;
  }

  get isAssistant(): boolean {
    return this.props.role === UserRole.Assistant;
  }

  get isPlayer(): boolean {
    return this.props.role === UserRole.Player;
  }

  get userCourses(): { course: number; status: UserCourseStatus; paid: boolean }[] {
    return this.props.userCourses;
  }

  getUserCourseStatus(courseId: number): string {
    let status: string;

    switch (this.props.userCourses.find(userCourse => userCourse.course === courseId)?.status) {
      case UserCourseStatus.Active:
        status = 'Approved';
        break;
      case UserCourseStatus.Inactive:
        status = 'Denied';
        break;
      case UserCourseStatus.AwaitingProfessorApproval:
        status = 'Pending Approval';
        break;
      case UserCourseStatus.AwaitingStudentConfirmation:
        status = 'Pending Confirmation';
        break;
      default:
        status = '-';
    }

    return status;
  }

  getUserCourseStatusClass(courseId: number): string {
    let status: string;

    switch (this.props.userCourses.find(userCourse => userCourse.course === courseId)?.status) {
      case UserCourseStatus.Active:
        status = 'active';
        break;
      case UserCourseStatus.Inactive:
        status = 'inactive';
        break;
      default:
        status = 'pending';
    }

    return status;
  }

  getUserCoursePaid(courseId: number): string {
    return this.props.userCourses.find(userCourse => userCourse.course === courseId)?.paid
      ? 'Paid'
      : 'Unpaid';
  }

  getUserCoursePaidClass(courseId: number): string {
    return this.props.userCourses.find(userCourse => userCourse.course === courseId)?.paid
      ? 'active'
      : 'inactive';
  }
}
